import { appConfirm } from '../services/appDialogs';
import React, { useState } from "react";
import Orbit3DViewer from "../components/Orbit3DViewer";
import { Upload, Image as ImageIcon, Download, Camera } from "lucide-react";
import { useTheme } from "../context/ThemeContext";
import { useToast } from "../context/ToastContext";

const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });
};

const Orbit3DPage = () => {
  const { getTheme } = useTheme();
  const toast = useToast();
  const [selectedImageBase64, setSelectedImageBase64] = useState(null);
  const [capturedImages, setCapturedImages] = useState([]);

  // 处理上传
  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      try {
        const base64 = await fileToBase64(file);
        setSelectedImageBase64(base64); // 存 Base64
        setCapturedImages([]); // 清空旧历史
      } catch (err) {
        console.error("图片读取失败", err);
        toast.error("图片读取失败", 0);
      }
    }
  };

  // 处理 Orbit3DViewer 传回来的截图 (Base64)
  const handleCapture = (newImageBase64) => {
    // 添加到历史记录
    setCapturedImages(prev => [newImageBase64, ...prev]);
  };

  // 下载图片
  const downloadImage = (base64, index) => {
    const link = document.createElement("a");
    link.href = base64;
    link.download = `3d_orbit_shot_${Date.now()}_${index}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="w-full h-full p-8 flex flex-col items-center overflow-auto custom-scrollbar" style={{ backgroundColor: 'var(--color-bg-overlay)' }}>
      <div className="max-w-5xl w-full flex flex-col items-center gap-8 pb-20">

        {/* Header */}
        <div className="text-center space-y-2 mt-8">
          <h1 className="text-4xl font-bold animate-in fade-in slide-in-from-bottom-4 duration-700"
            style={{ color: 'var(--color-text-primary)' }}>
            AI 360° 自由视角
          </h1>
          <p className="text-sm max-w-lg mx-auto" style={{ color: 'var(--color-text-secondary)' }}>
            上传静态角色或场景图，AI 实时构建环绕运镜。像 3D 软件一样旋转视角，捕捉完美瞬间。
          </p>
        </div>

        {/* 主内容区 */}
        {!selectedImageBase64 ? (
          // 1. 上传区域 (空状态)
          <div className="w-full max-w-xl aspect-video border-2 border-dashed rounded-2xl flex flex-col items-center justify-center gap-6 transition-all cursor-pointer group relative animate-in zoom-in-95 duration-500"
            style={{ borderColor: 'var(--color-border-primary)', backgroundColor: 'var(--color-bg-secondary)' }}>
            <input
              type="file"
              accept="image/*"
              onChange={handleUpload}
              className="absolute inset-0 opacity-0 cursor-pointer z-10"
            />
            <div className="w-20 h-20 rounded-full flex items-center justify-center transition-colors shadow-xl" style={{ backgroundColor: 'var(--color-bg-tertiary)' }}>
              <Upload size={32} style={{ color: 'var(--color-text-muted)' }} />
            </div>
            <div className="text-center space-y-1">
              <div className="font-bold text-lg" style={{ color: 'var(--color-text-primary)' }}>点击上传图片</div>
              <div className="text-xs" style={{ color: 'var(--color-text-muted)' }}>支持 JPG, PNG (推荐主体清晰的人物/静物)</div>
            </div>
          </div>
        ) : (
          // 2. 3D Viewer + 历史记录
          <div className="flex flex-col lg:flex-row gap-8 w-full items-start justify-center animate-in fade-in slide-in-from-bottom-8 duration-500">

            {/* 左侧：查看器 */}
            <div className="flex flex-col items-center gap-4 flex-1 w-full max-w-lg">
              <Orbit3DViewer
                initialImage={selectedImageBase64}
                onCapture={handleCapture}
              />
              <button
                onClick={async () => {
                    if((await appConfirm("确定要更换原图吗？当前进度将丢失。"))) {
                        setSelectedImageBase64(null);
                    }
                }}
                className="flex items-center gap-1 hover:underline py-2"
                style={{ color: 'var(--color-text-muted)' }}
              >
                <ImageIcon size={12} /> 更换原图
              </button>
            </div>

            {/* 右侧：截图画廊 */}
            {capturedImages.length > 0 && (
              <div className="w-full lg:w-64 flex flex-col gap-3 animate-in slide-in-from-right-8 duration-500">
                 <div className="text-xs font-bold uppercase tracking-wider mb-2 flex items-center gap-2" style={{ color: 'var(--color-text-secondary)' }}>
                    <Camera size={14} /> 截图记录 ({capturedImages.length})
                 </div>
                 <div className="grid grid-cols-2 lg:grid-cols-1 gap-3 max-h-[500px] overflow-y-auto custom-scrollbar pr-2">
                    {capturedImages.map((img, idx) => (
                        <div className="relative group rounded-lg overflow-hidden border" style={{ backgroundColor: 'var(--color-bg-overlay)', borderColor: 'var(--color-border-primary)' }}>
                            <img src={img} className="w-full aspect-video object-cover" />
                            <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2" style={{ backgroundColor: 'var(--overlay-light)' }}>
                                <button
                                    onClick={() => downloadImage(img, idx)}
                                    className="p-1.5 rounded-full transition-transform"
                                    style={{ backgroundColor: 'white', color: 'black' }}
                                    title="下载"
                                >
                                    <Download size={14} />
                                </button>
                            </div>
                        </div>
                    ))}
                 </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Orbit3DPage;
